import { useEffect, useState } from "react";
import { Box, Typography, Divider, Button } from "@mui/material";
import { useGetAdvisorPublicActivityFeed } from "services/feed";
import { AccountStub } from "protogen/common_pb";
import { FeedEntry, Medium } from "protogen/feed_pb";
import { UserAvatar } from "components/common/CurrentUserAvatar";
import DateDisplay from "components/common/DateDisplay";
import LinkRouter from "components/navigation/LinkRouter";
import FlexBox from "components/common/FlexBox";
import TruncatedText from "components/common/TruncatedText";
import useIsMobile from "components/hooks/useIsMobile";
import { BADGE_CONFIG, DEFAULT_BADGE_COLOR } from "common/badgeConfig";

interface AdvisorActivityFeedProps {
  advisorRef: string;
}

const getActivityDescription = (
  entry: FeedEntry,
  displayName: string,
): string => {
  switch (entry.medium) {
    case Medium.FORUM_POST:
      return `${displayName} posted`;
    case Medium.FORUM_COMMENT:
      return `${displayName} commented on`;
    case Medium.DOCUMENT:
      return `${displayName} shared`;
    case Medium.PLAYBOOK:
      return `${displayName} shared`;
    case Medium.BADGE:
      return `${displayName} earned`;
    default:
      return "";
  }
};

const getActivityTitle = (entry: FeedEntry): string => {
  switch (entry.medium) {
    case Medium.FORUM_POST:
      return entry.forumPost?.postTitle || "Forum post";
    case Medium.FORUM_COMMENT:
      return entry.forumComment?.postTitle || "Forum post";
    case Medium.DOCUMENT:
      return entry.document?.title || "Document";
    case Medium.PLAYBOOK:
      return entry.playbook?.playbookName || "Playbook";
    case Medium.BADGE:
      return entry.badge?.badgeName || "Badge";
    default:
      return "Activity";
  }
};

const getActivityLink = (entry: FeedEntry): string => {
  switch (entry.medium) {
    case Medium.FORUM_POST:
      return `/community/${encodeURIComponent(entry.forumPost?.postRef || "")}`;
    case Medium.FORUM_COMMENT:
      return `/community/${encodeURIComponent(
        entry.forumComment?.postRef || "",
      )}?commentRef=${encodeURIComponent(
        entry.forumComment?.commentRef || "",
      )}`;
    case Medium.DOCUMENT:
      return `/documents/${encodeURIComponent(entry.document?.uuid || "")}`;
    case Medium.PLAYBOOK:
      return `/tools/agents/playbooks/${encodeURIComponent(
        entry.playbook?.playbookRef || "",
      )}`;
    default:
      return "#";
  }
};

const ActivityEntryItem = ({
  entry,
  accountMap,
}: {
  entry: FeedEntry;
  accountMap: Record<string, AccountStub>;
}) => {
  const actor = accountMap[entry.senderRef];
  const actorName = actor?.firstName ? `${actor.firstName}` : "Advisor";

  const description = getActivityDescription(entry, actorName);
  const activityTitle = getActivityTitle(entry);
  const activityLink = getActivityLink(entry);
  const isMobile = useIsMobile();

  if (entry.medium === Medium.BADGE) {
    const badgeConfig = entry.badge?.badgeName
      ? BADGE_CONFIG[entry.badge.badgeName]
      : null;
    const BadgeIcon = badgeConfig?.icon;
    const badgeColor =
      badgeConfig?.color === DEFAULT_BADGE_COLOR
        ? undefined
        : badgeConfig?.color;

    return (
      <Box sx={{ display: "flex", gap: "12px", width: "100%" }}>
        {!isMobile && actor && (
          <Box
            sx={{
              flexShrink: 0,
              width: 24,
              height: 24,
            }}
          >
            <UserAvatar user={actor} size={24} clickable={false} />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
            width: "100%",
          }}
        >
          <FlexBox justifyContent="space-between" alignItems="center">
            <FlexBox alignItems="center" gap="8px">
              <Typography variant="bodyHeavy">{description}</Typography>
              {BadgeIcon && (
                <Box
                  sx={(theme) => ({
                    color: badgeColor || theme.palette.text.secondary,
                    display: "flex",
                    alignItems: "center",
                  })}
                >
                  <BadgeIcon size={16} />
                </Box>
              )}
              <TruncatedText
                variant="bodyHeavy"
                sx={(theme) => ({
                  color: badgeColor || theme.palette.text.secondary,
                })}
              >
                {badgeConfig?.label || activityTitle}
              </TruncatedText>
            </FlexBox>
            <DateDisplay date={new Date(Number(entry.timestampSec) * 1000)} />
          </FlexBox>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: "12px", width: "100%" }}>
      {!isMobile && actor && (
        <Box
          sx={{
            flexShrink: 0,
            width: 24,
            height: 24,
          }}
        >
          <UserAvatar user={actor} size={24} clickable={false} />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          width: "100%",
        }}
      >
        <FlexBox justifyContent="space-between">
          <FlexBox alignItems="center" gap="8px">
            <TruncatedText variant="bodyHeavy">{description}</TruncatedText>
          </FlexBox>
          <DateDisplay date={new Date(Number(entry.timestampSec) * 1000)} />
        </FlexBox>

        <LinkRouter
          to={activityLink}
          sx={{
            padding: 0,
            display: "flex",
            minWidth: 0,
            ...(isMobile && { width: "100%" }),
          }}
        >
          <TruncatedText
            variant="bodyHeavy"
            color="primary.main"
            width={isMobile ? "100%" : undefined}
            // sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {activityTitle}
          </TruncatedText>
        </LinkRouter>
        {entry.medium === Medium.FORUM_COMMENT &&
          entry.forumComment?.commentPreview && (
            <TruncatedText
              variant="bodySmall"
              color="text.secondary"
              // sx={{
              //   width: "100%",
              //   overflow: "hidden",
              //   textOverflow: "ellipsis",
              // }}
            >
              {entry.forumComment.commentPreview}
            </TruncatedText>
          )}
      </Box>
    </Box>
  );
};

export const AdvisorActivityFeed = ({
  advisorRef,
}: AdvisorActivityFeedProps) => {
  const { request } = useGetAdvisorPublicActivityFeed();
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [accountMap, setAccountMap] = useState<Record<string, AccountStub>>({});
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string>("");
  const isMobile = useIsMobile();

  const loadEntries = async (cursor: string = "") => {
    setLoading(true);
    const response = await request({
      advisorRef: advisorRef,
      startCursor: cursor,
      pageSize: 10,
      direction: "desc",
    });
    if (response) {
      if (cursor) {
        setEntries((prev) => [...prev, ...response.entries]);
      } else {
        setEntries(response.entries);
      }

      const accountsMap: Record<string, AccountStub> = { ...accountMap };
      response.accounts.forEach((account) => {
        accountsMap[account.ref] = account;
      });
      setAccountMap(accountsMap);
      setHasMore(response.hasMore);
      setNextCursor(response.nextCursor);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (advisorRef) {
      loadEntries();
    }
  }, [advisorRef]);

  const handleLoadMore = () => {
    loadEntries(nextCursor);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{}}>
        <Typography variant={isMobile ? "h3" : "h4"}>
          Recent activity
        </Typography>
      </Box>

      <FlexBox
        variant={"default"}
        direction="column"
        sx={{
          padding: isMobile ? undefined : "16px 0",
          marginTop: isMobile ? "16px" : undefined,
          width: "100%",
        }}
      >
        {loading && entries.length === 0 ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="bodyHeavy">Loading activity...</Typography>
          </Box>
        ) : entries.length === 0 ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="bodyHeavy">No recent activity</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              flexDirection: "column",
              width: "100%",
            }}
          >
            {entries.map((entry, index) => (
              <Box
                key={`${entry.ref}-${index}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isMobile ? "16px" : "12px",
                  width: "100%",
                }}
              >
                <ActivityEntryItem
                  key={`${entry.ref}-${index}`}
                  entry={entry}
                  accountMap={accountMap}
                />
                {index !== entries.length - 1 && <Divider />}
              </Box>
            ))}
            {hasMore && (
              <Button
                onClick={handleLoadMore}
                disabled={loading}
                variant="text"
                sx={{ alignSelf: "center", height: "unset" }}
              >
                {loading ? "Loading..." : "Load more"}
              </Button>
            )}
          </Box>
        )}
      </FlexBox>
    </Box>
  );
};
